import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

import { api } from '../lib/api';
import { canWrite } from '../lib/mutate';
import { useAuth } from '../auth/AuthContext';
import { formatCount } from '../lib/format';
import { EmptyState, Panel, Spinner } from './ui';
import { CategoryDialog, type EditableCategory } from './CategoryDialog';

interface Reference {
  categories: EditableCategory[];
}

/** Departments, with the flags that decide how the till treats their products. */
export function DepartmentsPanel({ storeId }: { storeId: string }): React.JSX.Element {
  const { activeStore } = useAuth();
  const editable = canWrite(activeStore?.role);

  const [editing, setEditing] = useState<EditableCategory | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['reference', storeId],
    queryFn: () => api.get<Reference>(`/stores/${storeId}/reference`),
    staleTime: 5 * 60_000,
  });

  const categories = data?.categories ?? [];

  return (
    <>
      {editable && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="flex items-center gap-1.5 rounded bg-accent px-2.5 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover"
          >
            <Plus size={14} aria-hidden />
            Add department
          </button>
        </div>
      )}

      <Panel>
        {isPending ? (
          <Spinner label="Loading departments…" />
        ) : categories.length === 0 ? (
          <EmptyState>No departments yet.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">Departments</caption>
              <thead>
                <tr className="border-b border-rule">
                  <th className="qs-eyebrow py-2 pl-3 text-left">Department</th>
                  <th className="qs-eyebrow py-2 text-right">Products</th>
                  <th className="qs-eyebrow hidden py-2 text-left md:table-cell">Behaviour</th>
                  {editable && (
                    <th className="py-2 pr-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.categoryId} className="border-b border-rule last:border-0">
                    <td className="py-1.5 pl-3 text-xs">{category.name}</td>
                    <td className="qs-figure py-1.5 pr-3 text-right text-xs">
                      {formatCount(category.products)}
                    </td>
                    <td className="hidden py-1.5 md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {category.ageRestricted && <Flag label="Age restricted" tone="warn" />}
                        {!category.profitable && <Flag label="Not profitable" />}
                        {!category.discountApplicable && <Flag label="No discounts" />}
                        {category.profitable &&
                          category.discountApplicable &&
                          !category.ageRestricted && (
                            <span className="text-2xs text-ink-faint">Standard</span>
                          )}
                      </div>
                    </td>
                    {editable && (
                      <td className="py-1.5 pr-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(category);
                            setDialogOpen(true);
                          }}
                          className="rounded border border-rule px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Mounted only while it is open. This was previously keyed on the
          department id, which reset the form when a *different* one was opened
          but not when the same one was — so a cancelled checkbox change, or the
          message from a rejected save, came back on the next open. */}
      {dialogOpen && (
        <CategoryDialog
          storeId={storeId}
          category={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function Flag({ label, tone }: { label: string; tone?: 'warn' }): React.JSX.Element {
  return (
    <span
      className={
        tone === 'warn'
          ? 'rounded border border-warn/30 bg-warn-soft px-1.5 py-0.5 text-2xs text-warn'
          : 'rounded border border-rule px-1.5 py-0.5 text-2xs text-ink-muted'
      }
    >
      {label}
    </span>
  );
}
